function renderFormLayoutDetailsBranchBodyTemplate({
  plural,
  singular
}: {
  plural: string
  singular: string
}): string {
  return `      {initialData?.id != null && (
        <div
          className={cn(
            'sticky z-100 flex flex-row items-center justify-between gap-1 px-4 bg-background h-14 top-14 transition-shadow overflow-x-clip',
            {
              'shadow-modal/2': hasScrolled,
              'border-b border-border/80': !hasScrolled
            }
          )}
        >
          <div className="flex flex-1 items-center gap-1 w-full">
            <EntityMetadata
              data={initialData}
              entityType="${plural}"
              entityId={initialData.id}
              getVersionsAction={getVersionsBy${singular}Id}
              restoreVersionAction={restore${singular}Version}
            />
          </div>
        </div>
      )}

`
}

function renderEntityFormPageClassName(): string {
  return `className={cn('min-h-[calc(100svh-56px)] w-full gap-0', {
          'min-h-[calc(100svh-56px-56px)]': initialData?.id != null
        })}`
}

function renderFormTabsLayoutTabsTriggerTemplate({
  topLevelTabsClassName,
  map,
  mapTab
}: {
  topLevelTabsClassName: string
  map: string
  mapTab: string
}): string {
  return `      <Tabs value={entityForm.activeTab} onValueChange={(value) => void entityForm.setActiveTab(value as TabValue)} ${topLevelTabsClassName}>
        <div className="flex h-14 shrink-0 w-full bg-background border-b border-border/80 px-4 items-center">
          <TabsList className="bg-transparent border-none">
${map}
          </TabsList>
        </div>

        <main className="w-full flex-1 min-h-0 p-4">
${mapTab}
        </main>
      </Tabs>`
}

function renderFormTabContentDetailsTemplate({
  tabName,
  contentComponentName
}: {
  tabName: string
  contentComponentName: string
}): string {
  return `          <TabsContent value="${tabName}" className="h-full min-h-0 w-full flex-1">
            <${contentComponentName} state={entityForm} tab="${tabName}" />
          </TabsContent>`
}

function renderEntityFormBody({ contentComponentName }: { contentComponentName: string }): string {
  return `      <main className="w-full p-4 h-full">
        <${contentComponentName} state={entityForm} />
      </main>`
}

function renderEntityFormPage({
  showMetadataWidgetScrollThresholdImport,
  topLevelTabIconNamesLucide,
  topLevelTabsFieldTabs,
  showMetadataWidgetEntityMetadataImport,
  singular,
  actionImportPath,
  showMetadataWidgetAdmin,
  topLevelTabsFieldHookImportPath,
  hookName,
  hookImportPath,
  contentComponentName,
  schemaName,
  metadataOffsetConst,
  metadataScrollState,
  hasDraftFlex,
  metadataStrip,
  shellBody
}: {
  showMetadataWidgetScrollThresholdImport: string
  topLevelTabIconNamesLucide: string
  topLevelTabsFieldTabs: string
  showMetadataWidgetEntityMetadataImport: string
  singular: string
  actionImportPath: string
  showMetadataWidgetAdmin: string
  topLevelTabsFieldHookImportPath: string
  hookName: string
  hookImportPath: string
  contentComponentName: string
  schemaName: string
  metadataOffsetConst: string
  metadataScrollState: string
  hasDraftFlex: string
  metadataStrip: string
  shellBody: string
}): string {
  return `'use client'
${showMetadataWidgetScrollThresholdImport}
${topLevelTabIconNamesLucide}
import { Button } from '@admin/components/ui/button'
${topLevelTabsFieldTabs}import { PageHeader } from '@admin/components/shared/page-header'${showMetadataWidgetEntityMetadataImport}
import type { ${singular} } from '@admin/actions/${actionImportPath}'
${showMetadataWidgetAdmin}
${topLevelTabsFieldHookImportPath}
import { ${hookName} } from '${hookImportPath}'
import { ${contentComponentName} } from './${schemaName}-form-content'

${metadataOffsetConst}interface ${singular}FormProps {
  initialData?: ${singular}
  pageTitle: string
}

export function ${singular}Form({ initialData, pageTitle }: ${singular}FormProps) {
${metadataScrollState}  const entityForm = ${hookName}({ initialData })

  return (
    <>
      <PageHeader
        title={pageTitle}
        actions={
${hasDraftFlex}
        }
      />
${metadataStrip}${shellBody}
    </>
  )
}
`
}

function renderFormActionButtonsTemplate(): string {
  return `          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values, false), entityForm.handleErrors)()}
              disabled={!entityForm.canSubmitDraftAction || entityForm.isPending}
              aria-busy={entityForm.isPending && entityForm.submitIntent === 'draft'}
            >
              {entityForm.isPending && entityForm.submitIntent === 'draft'
                ? initialData?.published ? 'Unpublishing…' : 'Saving Changes…'
                : initialData?.published ? 'Unpublish' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values, true), entityForm.handleErrors)()}
              disabled={entityForm.isPending || !entityForm.canSubmitPublishAction}
              aria-busy={entityForm.isPending && entityForm.submitIntent === 'publish'}
            >
              {entityForm.isPending && entityForm.submitIntent === 'publish'
                ? initialData?.published ? 'Publishing Changes…' : 'Publishing…'
                : initialData?.published ? 'Publish Changes' : 'Publish'}
            </Button>
          </div>`
}

function renderFormActionButtonsDetailsTemplate(): string {
  return `          <Button
            type="button"
            onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values), entityForm.handleErrors)()}
            disabled={entityForm.isPending || !entityForm.isDirty}
            aria-busy={entityForm.isPending}
          >
            {entityForm.isPending ? 'Saving…' : initialData ? 'Update' : 'Create'}
          </Button>`
}

export function renderEntityFormPageRenderer(
  request:
    | {
        kind: 'renderFormLayoutDetailsBranchBodyTemplate'
        props: {
          plural: string
          singular: string
        }
      }
    | { kind: 'renderEntityFormPageClassName' }
    | {
        kind: 'renderFormTabsLayoutTabsTriggerTemplate'
        props: {
          topLevelTabsClassName: string
          map: string
          mapTab: string
        }
      }
    | {
        kind: 'renderFormTabContentDetailsTemplate'
        props: {
          tabName: string
          contentComponentName: string
        }
      }
    | {
        kind: 'renderEntityFormBody'
        props: {
          contentComponentName: string
        }
      }
    | {
        kind: 'renderEntityFormPage'
        props: {
          showMetadataWidgetScrollThresholdImport: string
          topLevelTabIconNamesLucide: string
          topLevelTabsFieldTabs: string
          showMetadataWidgetEntityMetadataImport: string
          singular: string
          actionImportPath: string
          showMetadataWidgetAdmin: string
          topLevelTabsFieldHookImportPath: string
          hookName: string
          hookImportPath: string
          contentComponentName: string
          schemaName: string
          metadataOffsetConst: string
          metadataScrollState: string
          hasDraftFlex: string
          metadataStrip: string
          shellBody: string
        }
      }
    | { kind: 'renderFormActionButtonsTemplate' }
    | { kind: 'renderFormActionButtonsDetailsTemplate' }
): string {
  switch (request.kind) {
    case 'renderFormLayoutDetailsBranchBodyTemplate':
      return renderFormLayoutDetailsBranchBodyTemplate(request.props)
    case 'renderEntityFormPageClassName':
      return renderEntityFormPageClassName()
    case 'renderFormTabsLayoutTabsTriggerTemplate':
      return renderFormTabsLayoutTabsTriggerTemplate(request.props)
    case 'renderFormTabContentDetailsTemplate':
      return renderFormTabContentDetailsTemplate(request.props)
    case 'renderEntityFormBody':
      return renderEntityFormBody(request.props)
    case 'renderEntityFormPage':
      return renderEntityFormPage(request.props)
    case 'renderFormActionButtonsTemplate':
      return renderFormActionButtonsTemplate()
    case 'renderFormActionButtonsDetailsTemplate':
      return renderFormActionButtonsDetailsTemplate()
  }

  throw new Error('Unsupported renderer request')
}
